// Map state shared between the Plan map and the Maps tab:
// - the chosen layer and the overlays switched on (remembered on this browser, and kept with the person through
//   GET/PUT /maps/settings, so XpeditionGO shows the same);
// - whether the map is dimmed so routes and tracks stand out (remembered on this browser);
// - whether OpenStreetMap points of interest are shown (Premium; remembered on this browser);
// - whether crosshairs mark the centre of the map (remembered on this browser);
// - where the map is looking (kept for the browser tab, so leaving Plan and coming back keeps the view, and
//   remembered on this browser, so a new session starts where the last one left off);
// - the area on screen, so the Maps tab can grey out layers that don't cover it.
import { useSyncExternalStore } from 'react';
import { apiFetch } from '@/api/client';
import type { MapLayer } from './layers';

export interface MapViewState {
  /** [lon, lat]. */
  center: [number, number];
  zoom: number;
}

/** An overlay switched on. */
export interface OverlaySetting {
  name: string;
  /** 0.1 to 1. */
  opacity: number;
}

export interface MapState {
  layerId: string | null;
  /** The overlays switched on, bottom first. */
  overlays: OverlaySetting[];
  /** The map drawn faded so routes and tracks stand out on busy layers. */
  dimmed: boolean;
  /** OpenStreetMap points of interest on the map (Premium, mapPois). */
  pois: boolean;
  /** Crosshairs mark the centre of the map, the point the position readout gives. */
  crosshairs: boolean;
  /** The elevation and statistics panel along the bottom of the Plan map is shown. */
  statsOpen: boolean;
  /** With several trails: their profiles on top of each other, or one after another ("day by day"). */
  statsLayout: 'overlay' | 'days';
  view: MapViewState | null;
  /** [west, south, east, north] in degrees, as last shown on the map. */
  area: [number, number, number, number] | null;
}

const LAYER_KEY = 'xped.map.layer';
const OVERLAYS_KEY = 'xped.map.overlays';
const DIM_KEY = 'xped.map.dim';
const POIS_KEY = 'xped.map.pois';
const CROSSHAIRS_KEY = 'xped.map.crosshairs';
const STATS_KEY = 'xped.plan.stats';
const STATS_LAYOUT_KEY = 'xped.plan.statsLayout';
const VIEW_KEY = 'xped.map.view';
const AREA_KEY = 'xped.map.area';

function read<T>(storage: () => Storage, key: string): T | null {
  try {
    return JSON.parse(storage().getItem(key) ?? 'null') as T | null;
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string, value: unknown) {
  try {
    storage().setItem(key, JSON.stringify(value));
  } catch {
    // Not persisted; still works for this page.
  }
}

const local = () => localStorage;
const session = () => sessionStorage;

let state: MapState = {
  layerId: read<string>(local, LAYER_KEY),
  overlays: read<OverlaySetting[]>(local, OVERLAYS_KEY) ?? [],
  dimmed: read<boolean>(local, DIM_KEY) === true,
  pois: read<boolean>(local, POIS_KEY) === true,
  crosshairs: read<boolean>(local, CROSSHAIRS_KEY) === true,
  statsOpen: read<boolean>(local, STATS_KEY) !== false,
  statsLayout: read<string>(local, STATS_LAYOUT_KEY) === 'days' ? 'days' : 'overlay',
  view: read<MapViewState>(session, VIEW_KEY) ?? read<MapViewState>(local, VIEW_KEY),
  area: read<MapState['area']>(session, AREA_KEY),
};
const listeners = new Set<() => void>();
const set = (next: MapState) => {
  state = next;
  for (const listener of listeners) listener();
};

export const getMapState = () => state;

export function useMapState(): MapState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}

export function setPois(pois: boolean) {
  write(local, POIS_KEY, pois);
  set({ ...state, pois });
}

export function setDimmed(dimmed: boolean) {
  write(local, DIM_KEY, dimmed);
  set({ ...state, dimmed });
}

export function setCrosshairs(crosshairs: boolean) {
  write(local, CROSSHAIRS_KEY, crosshairs);
  set({ ...state, crosshairs });
}

export function setStatsLayout(statsLayout: MapState['statsLayout']) {
  write(local, STATS_LAYOUT_KEY, statsLayout);
  set({ ...state, statsLayout });
}

export function setStatsOpen(statsOpen: boolean) {
  write(local, STATS_KEY, statsOpen);
  set({ ...state, statsOpen });
}

/** Called by the map as it moves. */
export function saveView(view: MapViewState, area: MapState['area']) {
  write(session, VIEW_KEY, view);
  write(local, VIEW_KEY, view);
  write(session, AREA_KEY, area);
  set({ ...state, view, area });
}

/**
 * Chooses a layer and brings it into view, as XpeditionGO's Maps tab and the web spec (§9.3) do: if the view is
 * outside the layer's area it moves to the layer's centre at its start (or lowest) zoom; if it is below the
 * layer's lowest zoom it zooms in, and above its highest it zooms out.
 */
export function selectLayer(layer: MapLayer) {
  write(local, LAYER_KEY, layer.id);
  set({ ...state, layerId: layer.id, view: viewFor(layer, state.view) });
  if (state.view) {
    write(session, VIEW_KEY, state.view);
    write(local, VIEW_KEY, state.view);
  }
  saveSettings();
}

/** Switches an overlay on (on top of the others) or off. */
export function toggleOverlay(overlay: MapLayer) {
  const on = state.overlays.some((o) => o.name === overlay.id);
  setOverlays(
    on
      ? state.overlays.filter((o) => o.name !== overlay.id)
      : [...state.overlays, { name: overlay.id, opacity: overlay.opacity }],
  );
}

export function setOverlayOpacity(name: string, opacity: number) {
  setOverlays(state.overlays.map((o) => (o.name === name ? { ...o, opacity } : o)));
}

function setOverlays(overlays: OverlaySetting[]) {
  write(local, OVERLAYS_KEY, overlays);
  set({ ...state, overlays });
  saveSettings();
}

let saveTimer: ReturnType<typeof setTimeout> | undefined;

/** Keeps the choice with the person (shortly after the last change, so a slider sends once). */
function saveSettings() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    void apiFetch('/maps/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ layer: state.layerId, overlays: state.overlays }),
    }).catch(() => undefined);
  }, 800);
}

/**
 * After signing in: the map and overlays kept with the person (chosen here or in XpeditionGO). [overlayIds] moves
 * a layer that is now an overlay (it was a map before overlays existed) into the overlays.
 */
let settingsLoaded = false;

export async function loadSettings(overlayIds: ReadonlySet<string>) {
  if (settingsLoaded) return;
  settingsLoaded = true;
  let layerId = state.layerId;
  let overlays = state.overlays;
  try {
    const response = await apiFetch('/maps/settings');
    if (response.ok) {
      const saved = (await response.json()) as { layer?: string | null; overlays?: OverlaySetting[] };
      if (saved.layer || saved.overlays?.length) {
        layerId = saved.layer ?? null;
        overlays = saved.overlays ?? [];
      }
    }
  } catch {
    // Offline or signed out: this browser's choice.
  }
  const moved = layerId !== null && overlayIds.has(layerId);
  if (moved && !overlays.some((o) => o.name === layerId))
    overlays = [...overlays, { name: layerId!, opacity: 1 }];
  if (moved) layerId = null;
  write(local, LAYER_KEY, layerId);
  write(local, OVERLAYS_KEY, overlays);
  set({ ...state, layerId, overlays });
  if (moved) saveSettings();
}

export function viewFor(layer: MapLayer, view: MapViewState | null): MapViewState | null {
  if (!view)
    return layer.isDefault ? null : { center: centreOf(layer), zoom: layer.startZoom ?? layer.minZoom };
  const [w, s, e, n] = layer.extent;
  const [lon, lat] = view.center;
  if (lon < w || lon > e || lat < s || lat > n) {
    return { center: centreOf(layer), zoom: Math.max(layer.startZoom ?? layer.minZoom, layer.minZoom) };
  }
  const zoom = Math.min(Math.max(view.zoom, layer.minZoom), layer.maxZoom);
  return zoom === view.zoom ? view : { ...view, zoom };
}

function centreOf(layer: MapLayer): [number, number] {
  const [w, s, e, n] = layer.extent;
  const c = layer.centre;
  return c && c[0] >= w && c[0] <= e && c[1] >= s && c[1] <= n ? c : [(w + e) / 2, (s + n) / 2];
}
